import React from "react";
import { useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import { Form, Formik, FormikProps } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { Button } from "src/components/button/button";
import { Input } from "src/components/input/input";
import { ResetPasswordInput, ResetPasswordPayload } from "src/generated/types";

/*
 * GraphQL
 */

export const RESET_PASSWORD_MUTATION = gql`
    mutation ResetPassword($input: ResetPasswordInput!) {
        resetPassword(input: $input) {
            ok
        }
    }
`;

/*
 * Types
 */

interface ApolloData {
    resetPassword: ResetPasswordPayload;
}

interface ApolloMutationVars {
    input: ResetPasswordInput;
}

interface FormValues {
    email: string;
}

/*
 * Main
 */

export const PasswordResetForm = (): React.ReactElement => {
    const [resetPassword, { loading }] = useMutation<ApolloData, ApolloMutationVars>(RESET_PASSWORD_MUTATION);

    const initialValues: FormValues = { email: "" };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(values, { resetForm, setSubmitting }): void => {
                resetPassword({
                    variables: { input: { email: values.email } },
                })
                    .then(() => {
                        toast.success("Email triggered.");
                    })
                    .catch(() => {
                        toast.error("Uh oh! Please contact an admin.");
                    });

                setSubmitting(false);
                resetForm();
            }}
            validationSchema={Yup.object().shape({
                email: Yup.string().email("Invalid email address").required("Required"),
            })}
        >
            {(formik: FormikProps<FormValues>) => (
                <Form>
                    <Input
                        autoComplete="username"
                        data-testid="email"
                        id="email"
                        name="email"
                        placeholder="Enter Your Email"
                    />
                    <Button
                        disabled={formik.isSubmitting || !formik.isValid || loading}
                        type="submit"
                        variation="secondary"
                    >
                        Send Password Reset Link
                    </Button>
                </Form>
            )}
        </Formik>
    );
};
